import { ActionTree, GetterTree, MutationTree } from 'vuex';

import {
  BusinessOnboardingAccountForm,
  BusinessOnboardingLink,
  BusinessOnboardingNavLink,
  BusinessOnboardingState,
  BusinessOnboardingStoreDetailsForm,
  BusinessOnboardingYourDigitalCoinsForm,
  EBusinessOnboardingFlow
} from '@/models/business-onboarding';
import { IInternationalization } from '@/models/internationalization';

import APIService from '@/api';

import { getLabelInfo } from '@/utils/getLabelInfo';

import { IRouterProps } from '@/router/interfaces/router-props.interface';
import {
  getOauthState,
  isExternalOauthSessionOnline
} from '@/router/router-utils';

import { getSessionSignup } from '@/views/Signup/session';

import { colorDefaultData } from '@/componentsV2/molecules/ColorPicker/colorsDictionary';

import { RootState } from '../types';
import Module from '../interfaces/module.interface';
import { configWidgetFormDataFactory } from '@/views/Onboarding/helpers';
import { IWidgetSettingsFormData } from '@/models/Widget/settings';
import segmentIo from '@/utils/segment.io';
import flagKeys from '@/constants/flagKeys';
import { removeCountryCode } from '@/utils/string';
import {
  SmartBusinessSignupData,
  SmartBusinessSignupDataResponse
} from '@/models/smart-business-signup';

export const BUSINESS_ONBOARDING_STORAGE_KEY =
  '@twism:business-onboarding-store';

export const BUSINESS_ONBOARDING_UUID_CREATION =
  '@twism:uuid-business-creation';

export const NAV_LINKS: BusinessOnboardingLink[] = [
  {
    label: 'Your business',
    path: '/business-onboarding/your-store',
    key: 'your-store',
    routes: ['bo-your-store', 'bo-your-store-details']
  },
  {
    label: 'Category',
    path: '/business-onboarding/your-business-category',
    key: 'your-business-category',
    routes: ['bo-your-business-category']
  }
];

export const smartBusinessSignupDataDefault = {
  giftPercentageRecommendation: {
    enabled: false,
    percentage: 0
  },
  loyaltyPercentageRecommendation: {
    enabled: false,
    percentage: 0
  },
  generateBrandedCoins: {
    brandedCoinSelected: {
      url: ''
    },
    color: '',
    colorKey: '',
    images: []
  },
  offersRecommendation: {
    enabled: false,
    offers: []
  }
};

const initialState = (): BusinessOnboardingState => ({
  prefilledBusinessId: '',
  navLinks: APIService.token
    ? NAV_LINKS.filter(({ key }) => key !== 'your-account')
    : NAV_LINKS,
  createdAccount: false,
  yourDigitalCoin: {
    form: {
      color: colorDefaultData.colorHex,
      colorKey: colorDefaultData.colorKey,
      coinName: '',
      coinShowFront: true,
      isLoadingImg: false,
      image: {
        imageUrl: '',
        imageName: ''
      },
      isOauthImage: false
    }
  },
  account: {
    form: {
      firstName: '',
      lastName: '',
      email: '',
      password: ''
    }
  },
  yourStore: {
    form: {
      country: {} as IInternationalization,
      operationType: ''
    }
  },
  storeDetails: {
    form: {
      providerConnection: {
        provider: '',
        link: ''
      },
      address: null,
      aditionalAddress: '',
      website: '',
      merchantId: '',
      businessName: '',
      locationName: '',
      phone: {
        code: '',
        iso: '',
        number: ''
      }
    }
  },
  yourBusinessCategory: {
    categories: [],
    form: {
      selectedCategory: {
        id: '',
        otherValue: ''
      }
    }
  },
  loyaltyWidget: {
    form: configWidgetFormDataFactory()
  },
  smartBusinessSignup: smartBusinessSignupDataDefault,
  generatedSmartBusinessResponse: {}
});

const getPrimaryOauthLocation = (locations: any[]) => {
  const primaryLocation = locations.find(location => location.primary);

  return primaryLocation || locations[locations.length - 1];
};

export const clearBusinessOnboardingDataFromLocalStorage = () => {
  if (localStorage.getItem(BUSINESS_ONBOARDING_STORAGE_KEY)) {
    localStorage.removeItem(BUSINESS_ONBOARDING_STORAGE_KEY);
  }
};

export const setBusinessOnboardingDataOnLocalStorage = (
  state: BusinessOnboardingState
) => {
  localStorage.setItem(BUSINESS_ONBOARDING_STORAGE_KEY, JSON.stringify(state));
};

export const getBusinessOnboardingDataFromLocalStorage = (): BusinessOnboardingState => {
  return JSON.parse(
    localStorage.getItem(BUSINESS_ONBOARDING_STORAGE_KEY) ||
      JSON.stringify(initialState())
  );
};

const checkValidAddress = (address: Record<string, any>) => {
  const requiredFields = ['address', 'city', 'zip'];

  const valuedFields = Object.entries(address)
    .filter(([key, value]) => requiredFields.includes(key) && value)
    .map(([key]) => key);

  return requiredFields.every(field => valuedFields.includes(field));
};

export const mapOauthStateToStoreDetailsForm = (
  oauthState: any
): Partial<BusinessOnboardingState['storeDetails']['form']> | void => {
  if (!oauthState || !oauthState.businessData) return;

  const { locations, name, website } = oauthState.businessData;

  const location =
    locations && locations.length ? getPrimaryOauthLocation(locations) : {};

  let locationData;
  if (checkValidAddress(location))
    locationData = {
      address: location.address,
      fullAddress: getLabelInfo(
        ['address', 'city', 'state', 'zip', 'country'],
        location
      ),
      postCode: location.zip,
      cityName: location.city,
      stateName: location.state,
      stateCode: location.state,
      countryName: oauthState.rawData.country_name || location.country,
      countryCode: oauthState.rawData.country_code || location.countryIso2
    };

  return {
    website,
    businessName: name,
    address: locationData,
    phone: {
      iso: (locationData && locationData.countryCode) || location.countryIso2,
      number: removeCountryCode(
        location.phone || oauthState.rawData.phone || ''
      ).trim(),
      code: location.phoneCode
    }
  };
};

export const loadYourStoreData = (store: any) => {
  const signUpSession = getSessionSignup();
  const oauthState = getOauthState();

  if (oauthState) {
    store.dispatch(
      'businessOnboarding/SET_OPERATION_TYPE',
      oauthState.provider.type
    );
  }

  if (signUpSession && signUpSession.internationalization) {
    store.dispatch(
      'businessOnboarding/SET_SELECTED_COUNTRY',
      signUpSession.internationalization
    );
  }
};

export const loadStoreDetailsData = (store: any) => {
  const oauthState = getOauthState();

  store.dispatch(
    'businessOnboarding/SET_STORE_DETAILS_FORM',
    mapOauthStateToStoreDetailsForm(oauthState)
  );
};

export const loadAccountFormData = (store: any) => {
  const oauthState = getOauthState();
  if (oauthState)
    store.dispatch('businessOnboarding/SET_ACCOUNT_FORM', {
      ...store.state.businessOnboarding.account.form,
      email:
        store.state.businessOnboarding.account.form.email ||
        oauthState.businessData.ownerMail
    });
};

export const loadCoinStoreData = (store: any) => {
  const oauthState = getOauthState();

  if (
    oauthState &&
    oauthState.businessData.logo &&
    !store.state.businessOnboarding.yourDigitalCoin.form.coinFrontImgUrlSaved &&
    store.state.flags.flags.includes(flagKeys.importBusinessLogo)
  ) {
    let logo = oauthState.businessData.logo;
    if (typeof logo === 'object') {
      logo = logo.url;
    }
    segmentIo.track('Coin Image Preloaded', {
      'Image Url': logo,
      'Signup Online': !!isExternalOauthSessionOnline()
    });
    store.dispatch('businessOnboarding/SET_YOUR_DIGITAL_COIN_FORM', {
      image: {
        imageUrl: logo,
        imageName: `${oauthState.businessData.name}.png`
      },
      isOauthImage: true,
      coinFrontImgUrlSaved: logo
    });
  }
};

const factory = async (context: IRouterProps) => {
  context.store.dispatch('businessOnboarding/INITIALIZE_STATE');
  const state: any = context.store.state;
  if (!state.businessOnboarding.storeDetails.form.confirmed) {
    loadYourStoreData(context.store);
    loadStoreDetailsData(context.store);
    loadAccountFormData(context.store);
  }
};

const getters: GetterTree<BusinessOnboardingState, RootState> = {
  getAccountFormData: state => state.account.form,
  getYourStoreForm: state => state.yourStore.form,
  getYourBusinessCategoryForm: state => state.yourBusinessCategory.form,
  getStoreDetailsForm: state => state.storeDetails.form,
  getYourDigitalCoinForm: state => state.yourDigitalCoin.form,
  getNavLinks: state => state.navLinks,
  getCreatedAccount: state => state.createdAccount,
  getCategories: state => state.yourBusinessCategory.categories,
  getSelectedCategory: state =>
    state.yourBusinessCategory.form.selectedCategory,
  getSelectedCountry: state => state.yourStore.form.country,
  getLoyaltyWidgetForm: state => state.loyaltyWidget.form,
  getFlow: state => state.flow,
  getBusinessOnboardingState: state => state,
  getSmartBusinessSignup: state => state.smartBusinessSignup,
  getGeneratedSmartBusinessResponse: state =>
    state.generatedSmartBusinessResponse
};

const actions: ActionTree<BusinessOnboardingState, RootState> = {
  INITIALIZE_STATE({ commit }) {
    commit('initializeState');
  },
  RESET_BUSINESS_ONBOARDING({ commit }) {
    commit('resetBusinessOnboarding');
  },
  RESET_OPERATION_TYPE_ONBOARDING({ commit }) {
    commit('resetOperationTypeOnboarding');
  },
  SET_PREFILLED_BUSINESS_ID({ commit }, payload: string) {
    commit('setPrefilledBusinessId', payload);
  },
  SET_CREATED_ACCOUNT({ commit }, payload: boolean) {
    commit('setCreatedAccount', payload);
  },
  TOOGLE_COIN({ commit }) {
    commit('setToogleCoin');
  },
  SET_YOUR_DIGITAL_COIN_FORM(
    { commit },
    payload: Partial<BusinessOnboardingYourDigitalCoinsForm>
  ) {
    commit('setYourDigitalCoinForm', payload);
  },
  SET_LOYALTY_WIDGET_FORM(
    { commit },
    payload: Partial<IWidgetSettingsFormData>
  ) {
    commit('setLoyaltyWidgetForm', payload);
  },
  SET_ACCOUNT_FORM(
    { commit },
    payload: Partial<BusinessOnboardingAccountForm>
  ) {
    commit('setAccountForm', payload);
  },
  SET_STORE_DETAILS_FORM(
    { commit },
    payload: Partial<BusinessOnboardingStoreDetailsForm>
  ) {
    commit('setStoreDetailsForm', payload);
  },
  SET_SELECTED_COUNTRY({ commit }, payload: IInternationalization) {
    commit('setSelectedCountry', payload);
  },
  SET_OPERATION_TYPE({ commit }, payload: string) {
    commit('setOperationType', payload);
  },
  SET_NAV_LINKS({ commit }, payload: BusinessOnboardingNavLink[]) {
    commit('setNavLinks', payload);
  },
  SET_CATEGORIES({ commit }, payload: Array<{ label: string; value: string }>) {
    commit('setCategories', payload);
  },
  SET_SELECTED_CATEGORY(
    { commit },
    payload: Partial<{ id: string; otherValue: string }>
  ) {
    commit('setSelectedCategory', payload);
  },
  SET_BUSINESS_LEAD_ID({ commit }, payload: string) {
    commit('setBusinessLeadId', payload);
  },
  SET_FLOW({ commit }, payload: EBusinessOnboardingFlow) {
    commit('setFlow', payload);
  },
  SET_SMART_BUSINESS_SIGNUP({ commit }, payload: SmartBusinessSignupData) {
    commit('setSmartBusinessSignup', payload);
  },
  SET_GENERATED_SMART_BUSINESS_RESPONSE(
    { commit },
    payload: SmartBusinessSignupDataResponse
  ) {
    commit('setGeneratedSmartBusinessResponse', payload);
  }
};

const mutations: MutationTree<BusinessOnboardingState> = {
  initializeState(state) {
    Object.assign(state, getBusinessOnboardingDataFromLocalStorage());
  },
  resetBusinessOnboarding(state) {
    const categories = state.yourBusinessCategory.categories;
    const navLinks =
      APIService.token && state.navLinks
        ? state.navLinks.filter(({ key }) => key !== 'your-account')
        : state.navLinks;

    Object.assign(state, {
      ...initialState(),
      yourBusinessCategory: {
        ...initialState().yourBusinessCategory,
        categories
      },
      navLinks
    });

    clearBusinessOnboardingDataFromLocalStorage();
  },
  resetOperationTypeOnboarding(state) {
    const newState = {
      yourStore: initialState().yourStore
    };

    Object.assign(state, {
      ...newState
    });

    setBusinessOnboardingDataOnLocalStorage({
      ...getBusinessOnboardingDataFromLocalStorage(),
      ...newState
    });
  },
  setToogleCoin(state) {
    state.yourDigitalCoin.form.coinShowFront = !state.yourDigitalCoin.form
      .coinShowFront;
  },
  setAccountForm(state, formData: Partial<BusinessOnboardingAccountForm>) {
    Object.assign(state.account.form, formData);
  },
  setSelectedCountry(state, selectedCountry: IInternationalization) {
    state.yourStore.form.country = selectedCountry;
  },
  setOperationType(state, opreationType: string) {
    state.yourStore.form.operationType = opreationType;
  },
  setNavLinks(state, navLinks: BusinessOnboardingNavLink[]) {
    state.navLinks = navLinks;
  },
  setFlow(state, flowName: EBusinessOnboardingFlow) {
    state.flow = flowName;
  },
  setStoreDetailsForm(
    state,
    formData: Partial<BusinessOnboardingStoreDetailsForm>
  ) {
    Object.assign(state.storeDetails.form, formData);
  },
  setCategories(state, categories: Array<{ label: string; value: string }>) {
    state.yourBusinessCategory.categories = categories;
  },
  setSelectedCategory(
    state,
    selectedCategory: Partial<{ id: string; otherValue: string }>
  ) {
    Object.assign(
      state.yourBusinessCategory.form.selectedCategory,
      selectedCategory
    );
  },
  setBusinessLeadId(state, businessLeadId: string) {
    state.storeDetails.form.businessLeadId = businessLeadId;
  },
  setYourDigitalCoinForm(
    state,
    formData: Partial<BusinessOnboardingYourDigitalCoinsForm>
  ) {
    Object.assign(state.yourDigitalCoin.form, formData);
  },
  setLoyaltyWidgetForm(state, formData: Partial<IWidgetSettingsFormData>) {
    Object.assign(state.loyaltyWidget.form, formData);
  },
  setPrefilledBusinessId(state, businessId: string) {
    state.prefilledBusinessId = businessId;
  },
  setCreatedAccount(state, hasCreatedAccount: boolean) {
    state.createdAccount = hasCreatedAccount;
  },
  setSmartBusinessSignup(state, data: SmartBusinessSignupData) {
    state.smartBusinessSignup = data;
  },
  setGeneratedSmartBusinessResponse(
    state,
    data: SmartBusinessSignupDataResponse
  ) {
    state.generatedSmartBusinessResponse = data;
  }
};

export const businessOnboarding: Module<BusinessOnboardingState, RootState> = {
  namespaced: true,
  initializer: {
    factory,
    provide: 'BusinessOnboardingStore',
    on: 'BeforeEachRoute'
  },
  state: initialState(),
  getters,
  actions,
  mutations
};
